import { Controller } from "@hotwired/stimulus"

// Excel keys for the Tarifvertrag review table. The reviewer walks a wage table
// column by column against the PDF, which is spreadsheet work: Enter commits and
// moves down the same column, Shift+Enter moves up, the arrow keys do the same,
// Escape puts the cell back, and focusing a cell selects it so typing overwrites.
//
// Tab is left alone — the browser's own tab order across a table row is already
// Excel's "commit and move right".
//
// Cells stay plain inputs inside one form: nothing is saved per cell, the whole
// draft is committed once with Übernehmen (an amount only means something next to
// the others in its row).
export default class extends Controller {
  connect() {
    this.original = new WeakMap()
  }

  select(event) {
    const cell = event.target
    if (!this.isCell(cell)) return

    this.original.set(cell, cell.value)
    cell.select?.()
  }

  keydown(event) {
    const cell = event.target
    if (!this.isCell(cell)) return

    if (event.key === "Escape") {
      this.revert(cell, event)
    } else if (event.key === "Enter" || event.key === "ArrowDown" || event.key === "ArrowUp") {
      const up = event.key === "ArrowUp" || (event.key === "Enter" && event.shiftKey)
      this.move(cell, up ? -1 : 1, event)
    }
  }

  revert(cell, event) {
    if (!this.original.has(cell)) return

    event.preventDefault()
    cell.value = this.original.get(cell)
    // The row total followed the discarded keystrokes; put it back too, or it keeps
    // showing a sum for a value that is no longer in the cell.
    this.recalculate(cell.closest("tr"))
    cell.select?.()
  }

  // The same column of the same table, one row further on. Staying inside the table
  // is deliberate: a column means "Lohn" only within one Lohngruppe.
  move(cell, offset, event) {
    const row = cell.closest("tr")
    const table = cell.closest("table")
    if (!row || !table) return

    const rows = Array.from(table.querySelectorAll("tbody tr"))
    const target = rows[rows.indexOf(row) + offset]
    if (!target) return

    const column = Array.from(row.querySelectorAll("[data-grid-cell]")).indexOf(cell)
    const next = target.querySelectorAll("[data-grid-cell]")[column]
    if (!next) return

    event.preventDefault()
    next.focus()
  }

  // The row's Gesamttarifstundenlohn, kept live: comparing the sum against the one
  // printed in the PDF is what catches a misread column, and a total that went stale
  // the moment a cent was corrected would be worse than none.
  total(event) {
    this.recalculate(event.target.closest("tr"))
  }

  recalculate(row) {
    const output = row?.querySelector("[data-grid-total]")
    if (!output) return

    const sum = Array.from(row.querySelectorAll("[data-grid-amount]")).reduce(
      (acc, input) => acc + this.number(input.value),
      0,
    )
    output.textContent = sum > 0 ? sum.toFixed(2).replace(".", ",") : "–"
  }

  // German input as well as plain decimals — the same latitude
  // AiExtraction::GermanNumberParsing gives on the server, so the preview never
  // disagrees with what gets saved. That includes a grouped amount: a reviewer reading
  // a Monatsgehalt off the PDF types (or pastes) "3.980,50", the sanitizer stores 3980.5
  // for it, and without stripping the thousands dot here the live total showed 3,98 for
  // a row that saves as 3980,50 — the one number this column exists to check.
  number(value) {
    let text = String(value).replace(/\s/g, "")
    if (text.includes(",")) text = text.replace(/\./g, "").replace(",", ".")

    const parsed = parseFloat(text)
    return Number.isFinite(parsed) ? parsed : 0
  }

  isCell(element) {
    return element?.hasAttribute?.("data-grid-cell")
  }
}
